import { useTranslation } from "react-i18next";
import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import { Node, Path, Flow, Field, Spark } from "@/components/primitives/Primitives";
import { ArrowUpRight } from "lucide-react";

// Primitive kinds are keyed by position (order matches translation array).
const primitives = ["flow", "field", "path", "spark", "node"];

function Primitive({ kind }) {
    const cls = "w-full h-full text-foreground";
    if (kind === "node") return <Node className={cls} size={220} variant="brand" />;
    if (kind === "path") return <Path className={cls} variant="brand" />;
    if (kind === "flow") return <Flow className={cls} variant="brand" />;
    if (kind === "field") return <Field className={cls} variant="brand" />;
    return <Spark className={cls} variant="brand" />;
}

export default function Products() {
    const { t } = useTranslation();
    const products = t("body.products.items", { returnObjects: true }) || [];
    const ctaWalkthrough = t("body.products.ctaWalkthrough");
    return (
        <Layout title="Products — Ventara" description="Sonare, Atlas, Foreground, Lumen, Compass — the surfaces of the quiet stack.">
            <PageHero i18nKey="products"
                eyebrow="Products"
                title={<>Surfaces of<br />the quiet stack.</>}
                lead="Five products that share a philosophy and a spine. Built to disappear into the work, and to reappear at the moment they matter."
            />

            <Section className="pb-32 space-y-24">
                {products.map((p, idx) => (
                    <Reveal key={p.name}>
                        <article className={`grid lg:grid-cols-12 gap-16 items-center ${idx % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`} data-testid={`product-${p.name.toLowerCase()}`}>
                            <div className="lg:col-span-6">
                                <div className="rounded-3xl border border-border bg-muted/30 aspect-[4/3] p-12 flex items-center justify-center opacity-80">
                                    <Primitive kind={primitives[idx] || "node"} />
                                </div>
                            </div>
                            <div className="lg:col-span-6">
                                <p className="label">{p.tag}</p>
                                <h2 className="font-serif font-light text-5xl sm:text-6xl mt-4 tracking-tight">{p.name}</h2>
                                <p className="font-serif text-2xl mt-6 text-muted-foreground italic">{p.line}</p>
                                <p className="prose-body mt-6 max-w-lg">{p.body}</p>
                                <button className="mt-8 inline-flex items-center gap-2 text-sm border-b border-foreground/40 pb-1 hover:border-foreground transition-colors" data-testid={`product-${p.name.toLowerCase()}-cta`}>
                                    {ctaWalkthrough} <ArrowUpRight className="h-4 w-4" />
                                </button>
                            </div>
                        </article>
                    </Reveal>
                ))}
            </Section>
        </Layout>
    );
}
